import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["ageRange", "ageValue", "ageHidden", "clearFilters"]

    connect() {
        this.lastCheckedFrequency = null
        this.filterTimeout = null
        this.initializeAgeSlider()
        this.initializeFilters()
    }

    initializeAgeSlider() {
        if (!this.hasAgeRangeTarget || !this.hasAgeValueTarget) {
            return
        }

        const range = this.ageRangeTarget
        const valueEl = this.ageValueTarget
        const hidden = this.hasAgeHiddenTarget ? this.ageHiddenTarget : null

        const THUMB_W = 18
        const updateUI = () => {
            const min = parseInt(range.min || '0')
            const max = parseInt(range.max || '99')
            const val = parseInt(range.value || '0')

            const rect = range.getBoundingClientRect()
            const trackW = rect.width
            
            const ratio = (val - min) / (max - min || 1)
            const posPx = ratio * (trackW - THUMB_W) + (THUMB_W / 2)
            
            range.style.backgroundSize = `${posPx}px 100%`
            
            valueEl.style.left = `${posPx}px`
            
            if (val === 0) {
                valueEl.textContent = 'Visi'
                if (hidden) hidden.value = ''
            } else {
                valueEl.textContent = String(val)
                if (hidden) hidden.value = String(val)
            }
        }
        updateUI()
        
        range.addEventListener('input', () => {
            updateUI()
            this.debouncedApplyFilters()
        })
        
        window.addEventListener('resize', updateUI)
    }

    initializeFilters() {
        const filterInputs = this.element.querySelectorAll('.product-filter input:not([name="frequency"]):not([name="age_range_ui"]), .product-filter select')
        filterInputs.forEach(input => {
            input.addEventListener('change', () => {
                this.debouncedApplyFilters()
            })
        })

        const frequencyRadios = this.element.querySelectorAll('.product-filter input[name="frequency"]')
        
        frequencyRadios.forEach(radio => {
            radio.addEventListener('click', (e) => {
                if (this.lastCheckedFrequency === radio) {
                    radio.checked = false
                    this.lastCheckedFrequency = null
                    this.debouncedApplyFilters()
                } else {
                    this.lastCheckedFrequency = radio
                    this.debouncedApplyFilters()
                }
            })
        })

        if (this.hasClearFiltersTarget) {
            this.clearFiltersTarget.addEventListener('click', (e) => {
                e.preventDefault()
                this.clearFilters()
            })
        }
        
        this.applyFilters()
    }

    debouncedApplyFilters() {
        if (this.filterTimeout) {
            clearTimeout(this.filterTimeout)
        }
        this.filterTimeout = setTimeout(() => {
            this.applyFilters()
        }, 100)
    }

    applyFilters() {
        const filters = {}
        
        const availabilityRadio = this.element.querySelector('.product-filter input[name="available"]:checked')
        if (availabilityRadio && availabilityRadio.value && availabilityRadio.value !== 'all') {
            filters.availability = availabilityRadio.value === '1' ? 'active' : 'no-spaces'
        }
        
        const frequencyRadio = this.element.querySelector('.product-filter input[name="frequency"]:checked')
        if (frequencyRadio && frequencyRadio.value) {
            filters.frequency = frequencyRadio.value
        }

        if (this.hasAgeHiddenTarget && this.ageHiddenTarget.value) {
            filters.age = parseInt(this.ageHiddenTarget.value)
        }

        const instructorSelect = this.element.querySelector('.product-filter select[name="instructor"]')
        if (instructorSelect && instructorSelect.value) {
            filters.instructor = instructorSelect.value
        }

        this.filterScheduleCards(filters)
    }

    filterScheduleCards(filters) {
        const scheduleCards = this.element.querySelectorAll('.schedule-card')
        const resultsCounter = this.element.querySelector('.results-counter')
        let visibleCount = 0
        
        scheduleCards.forEach(card => {
            let shouldShow = true

            if (filters.age) {
                const ageRange = card.dataset.ageRange
                if (ageRange && ageRange !== 'N/A') {
                    if (!this.matchesAgeFilter(filters.age, ageRange)) {
                        shouldShow = false
                    }
                }
            }

            if (filters.instructor && shouldShow) {
                const cardInstructor = card.dataset.instructor
                if (cardInstructor && cardInstructor !== 'N/A') {
                    const normalizeString = (str) => {
                        return str.trim().toLowerCase()
                            .replace(/\s+/g, ' ')
                            .replace(/\u00A0/g, ' ')
                            .replace(/\u2000-\u200B/g, ' ')
                            .trim()
                    }
                    
                    const normalizedSelected = normalizeString(filters.instructor)
                    const normalizedCard = normalizeString(cardInstructor)
                    
                    if (normalizedSelected !== normalizedCard) {
                        shouldShow = false
                    }
                }
            }

            if (filters.availability && shouldShow) {
                const cardStatus = card.dataset.status || 'active'
                
                if (filters.availability === 'active') {
                    if (cardStatus !== 'active') {
                        shouldShow = false
                    }
                } else if (filters.availability === 'no-spaces') {
                    const statusIsNoSpacesGroup = (cardStatus === 'no-spaces' || cardStatus === 'early-registration')
                    if (!statusIsNoSpacesGroup) {
                        shouldShow = false
                    }
                }
            }

            if (filters.frequency && shouldShow) {
                const cardFrequency = card.dataset.frequency || '0'
                if (cardFrequency !== filters.frequency) {
                    shouldShow = false
                }
            }

            if (shouldShow) {
                card.style.display = 'block'
                visibleCount++
            } else {
                card.style.display = 'none'
            }
        })

        if (resultsCounter) {
            const countSpan = resultsCounter.querySelector('[data-sidebar-filter-target="count"]')
            if (countSpan) {
                countSpan.textContent = visibleCount
            } else {
                const p = resultsCounter.querySelector('p')
                if (p) {
                    p.textContent = `Rasta ${visibleCount} rezultatų`
                }
            }
        }
    }

    matchesAgeFilter(selectedAge, ageRangeText) {
        const parts = ageRangeText.split('-')
        if (parts.length === 2) {
            const minAge = parseInt(parts[0])
            const maxAge = parseInt(parts[1])
            return selectedAge >= minAge && selectedAge <= maxAge
        } else if (parts.length === 1) {
            const exactAge = parseInt(parts[0])
            return selectedAge === exactAge
        }
        return false
    }

    clearFilters() {
        const availabilityRadios = this.element.querySelectorAll('.product-filter input[name="available"]')
        availabilityRadios.forEach(radio => {
            radio.checked = radio.value === 'all'
        })

        const frequencyRadios = this.element.querySelectorAll('.product-filter input[name="frequency"]')
        if (frequencyRadios.length > 0) {
            frequencyRadios.forEach(radio => {
                radio.checked = false
            })
        }

        if (this.hasAgeRangeTarget) {
            this.ageRangeTarget.value = '0'
            const min = parseInt(this.ageRangeTarget.min || '0')
            const max = parseInt(this.ageRangeTarget.max || '99')
            const val = 0
            const rect = this.ageRangeTarget.getBoundingClientRect()
            const trackW = rect.width
            const THUMB_W = 18
            const ratio = (val - min) / (max - min || 1)
            const posPx = ratio * (trackW - THUMB_W) + (THUMB_W / 2)
            this.ageRangeTarget.style.backgroundSize = `${posPx}px 100%`
        }
        if (this.hasAgeValueTarget) {
            this.ageValueTarget.style.left = '0px'
            this.ageValueTarget.textContent = 'Visi'
        }
        if (this.hasAgeHiddenTarget) {
            this.ageHiddenTarget.value = ''
        }

        const instructorSelect = this.element.querySelector('.product-filter select[name="instructor"]')
        if (instructorSelect && instructorSelect.tomselect) {
            instructorSelect.tomselect.setValue('', true)
        } else if (instructorSelect) {
            instructorSelect.value = ''
        }

        this.applyFilters()
    }

    disconnect() {
        
    }
}
